import { configClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import {
  toAction,
  toArchivePreview,
  toAttachment,
  toChange,
  toChecks,
  toDiffItem,
  toNode,
  toNodeDetail,
  toRelease,
  toResolved,
  toSection,
  toValue,
} from '../mappers/taxonomy-studio.mappers';
import type {
  ActionApi,
  ArchivePreviewApi,
  AttachmentApi,
  ChangeApi,
  ChecksApi,
  DiffApi,
  NodeDetailApi,
  NodeListApi,
  PageApi,
  PermissionsApi,
  ReleaseApi,
  ResolvedApi,
  RestorePreviewApi,
  SectionApi,
  ValueApi,
} from '../types/taxonomy-studio.api.types';
import type {
  ArchivePreview,
  Attachment,
  Change,
  DraftAction,
  DraftChecks,
  NodeDetail,
  Page,
  Release,
  ReleaseDiff,
  ReleaseRef,
  ResolvedNode,
  Section,
  TaxonomyNode,
  TaxonomyValue,
} from '../types/taxonomy-studio.types';

const api = endpoints.taxonomyStudio;

/** Builds URLSearchParams from a flat record, dropping empty / undefined entries. */
function params(record: Record<string, string | number | boolean | null | undefined>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(record)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  return sp;
}

/** The open draft, or null when none is open. */
export async function fetchDraft(): Promise<Release | null> {
  const wire = await configClient.get(api.draft()).json<ReleaseApi | null>();
  return wire ? toRelease(wire) : null;
}

/** Publish checks for the open draft. */
export async function fetchDraftChecks(): Promise<DraftChecks> {
  return toChecks(await configClient.get(api.draftChecks()).json<ChecksApi>());
}

/** The caller's own undoable actions in the draft, newest first. */
export async function fetchDraftActions(): Promise<DraftAction[]> {
  const wire = await configClient
    .get(api.draftActions(), { searchParams: params({ mine: true }) })
    .json<{ items: ActionApi[] }>();
  return wire.items.map(toAction);
}

/** Every release (draft included), newest first. */
export async function fetchReleases(): Promise<Release[]> {
  const wire = await configClient.get(api.releases()).json<{ items: ReleaseApi[] }>();
  return wire.items.map(toRelease);
}

export interface ChangeFilters {
  entityType?: string | undefined;
  op?: string | undefined;
  source?: string | undefined;
  q?: string | undefined;
  limit: number;
  offset: number;
}

/** One page of a release's changes (`seq` may be `draft`). */
export async function fetchReleaseChanges(seq: string, f: ChangeFilters): Promise<Page<Change>> {
  const wire = await configClient
    .get(api.releaseChanges(seq), {
      searchParams: params({
        entity_type: f.entityType,
        op: f.op,
        source: f.source,
        q: f.q,
        limit: f.limit,
        offset: f.offset,
      }),
    })
    .json<PageApi<ChangeApi>>();
  return { ...wire, items: wire.items.map(toChange) };
}

export interface AuditFilters {
  entityType?: string | undefined;
  entityKey?: string | undefined;
  nodeId?: number | undefined;
  sectionKey?: string | undefined;
  by?: string | undefined;
  since?: string | undefined;
  until?: string | undefined;
  q?: string | undefined;
  includeDraft?: boolean | undefined;
  limit: number;
  offset: number;
}

/** Audit trail across published releases (and the draft with `includeDraft`). */
export async function fetchChanges(f: AuditFilters): Promise<Page<Change>> {
  const wire = await configClient
    .get(api.changes(), {
      searchParams: params({
        entity_type: f.entityType,
        entity_key: f.entityKey,
        node_id: f.nodeId,
        section_key: f.sectionKey,
        by: f.by,
        since: f.since,
        until: f.until,
        q: f.q,
        include_draft: f.includeDraft ? true : undefined,
        limit: f.limit,
        offset: f.offset,
      }),
    })
    .json<PageApi<ChangeApi>>();
  return { ...wire, items: wire.items.map(toChange) };
}

/** Diff between two versions (`live`, `draft` or a number). */
export async function fetchDiff(
  from: string,
  to: string,
  limit: number,
  offset: number,
): Promise<ReleaseDiff> {
  const wire = await configClient
    .get(api.diff(), { searchParams: params({ from, to, limit, offset }) })
    .json<DiffApi>();
  return {
    fromSeq: wire.from.seq,
    toSeq: wire.to.seq,
    counts: wire.counts,
    total: wire.total,
    items: wire.items.map(toDiffItem),
  };
}

/** What restoring `seq` would change (first 200 items). */
export async function fetchRestorePreview(seq: number): Promise<ReleaseDiff> {
  const wire = await configClient.get(api.releaseRestorePreview(seq)).json<RestorePreviewApi>();
  return {
    fromSeq: wire.target.seq,
    toSeq: wire.target.seq,
    counts: wire.counts,
    total: wire.total,
    items: wire.items.map(toDiffItem),
  };
}

/** Children of `parentId` (null → vertical roots), sorted by name. */
export async function fetchNodeChildren(
  parentId: number | null,
  release: ReleaseRef,
  status: 'ALL' | 'ACTIVE',
): Promise<TaxonomyNode[]> {
  const wire = await configClient
    .get(api.nodes(), {
      searchParams: params({ parent_id: parentId ?? undefined, release, status }),
    })
    .json<NodeListApi>();
  return wire.items.map(toNode);
}

/** Name / id search; rows carry `pathText`. */
export async function searchNodes(
  q: string,
  release: ReleaseRef,
  status: 'ALL' | 'ACTIVE',
): Promise<TaxonomyNode[]> {
  const wire = await configClient
    .get(api.nodes(), { searchParams: params({ q, release, status }) })
    .json<NodeListApi>();
  return wire.items.map(toNode);
}

export async function fetchNode(id: number, release: ReleaseRef): Promise<NodeDetail> {
  const wire = await configClient
    .get(api.node(id), { searchParams: params({ release }) })
    .json<NodeDetailApi>();
  return toNodeDetail(wire);
}

/** Every attribute that applies at a node, with where it is attached. */
export async function fetchResolved(id: number, release: ReleaseRef): Promise<ResolvedNode> {
  const wire = await configClient
    .get(api.nodeResolved(id), { searchParams: params({ release, include: 'values' }) })
    .json<ResolvedApi>();
  return toResolved(wire);
}

export async function fetchArchivePreview(id: number): Promise<ArchivePreview> {
  const wire = await configClient
    .get(api.nodeArchivePreview(id), { searchParams: params({ release: 'draft' }) })
    .json<ArchivePreviewApi>();
  return toArchivePreview(wire);
}

export interface SectionFilters {
  release: ReleaseRef;
  q?: string | undefined;
  valueType?: string | undefined;
  status?: string | undefined;
  gmcField?: string | undefined;
  changed?: boolean | undefined;
  attachedTo?: number | undefined;
  limit: number;
  offset: number;
}

/** One page of attributes. */
export async function fetchSections(f: SectionFilters): Promise<Page<Section>> {
  const wire = await configClient
    .get(api.sections(), {
      searchParams: params({
        release: f.release,
        q: f.q,
        value_type: f.valueType,
        status: f.status,
        gmc_field: f.gmcField,
        changed: f.changed ? true : undefined,
        attached_to: f.attachedTo,
        sort: 'name',
        limit: f.limit,
        offset: f.offset,
      }),
    })
    .json<PageApi<SectionApi>>();
  return { ...wire, items: wire.items.map(toSection) };
}

export async function fetchSection(key: string, release: ReleaseRef): Promise<Section> {
  const wire = await configClient
    .get(api.section(key), { searchParams: params({ release }) })
    .json<SectionApi>();
  return toSection(wire);
}

/** All values of an attribute (active + archived; the list is not paginated). */
export async function fetchSectionValues(
  key: string,
  release: ReleaseRef,
): Promise<TaxonomyValue[]> {
  const wire = await configClient
    .get(api.sectionValues(key), { searchParams: params({ release, status: 'ALL' }) })
    .json<{ items: ValueApi[] }>();
  return wire.items.map(toValue);
}

export async function fetchSectionAttachments(
  key: string,
  release: ReleaseRef,
): Promise<Attachment[]> {
  const wire = await configClient
    .get(api.sectionAttachments(key), { searchParams: params({ release }) })
    .json<{ items: AttachmentApi[] }>();
  return wire.items.map(toAttachment);
}

/** Fetches an authenticated CSV (template / export / errors) as a Blob. */
export async function fetchCsvBlob(
  path: string,
  search?: Record<string, string | number | undefined>,
): Promise<Blob> {
  return configClient.get(path, { searchParams: params(search ?? {}) }).blob();
}

/** What the signed-in user may do in the studio. */
export async function fetchPermissions() {
  const w = await configClient.get(api.permissions()).json<PermissionsApi>();
  return {
    canRead: w.can_read,
    canWrite: w.can_write,
    canPublish: w.can_publish,
    canAdmin: w.can_admin,
    actor: w.actor,
  };
}
